import { useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyButton,
  FireflyIconButton,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { NoteEvents } from '~/analytics';
import { EditNoteAccordionFooterNetworkStatus } from '~/components/maud/patients/note/EditNoteAccordionFooterNetworkStatus';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useDeleteNoteMutation } from '~/store/services/notes/notes';
import { DeleteNoteModal } from '~/components/common/DeleteNoteModal';

const TOP_LEVEL_ID = 'add-note';
type Props = {
  dismissAccordion: () => void;
  existingNoteId: string | null;
  inquiryId?: string;
  isAnyNoteApiInFlight: boolean;
  isGetNoteDataLoading: boolean;
  isNoteAuthor: boolean;
  isGroupClassNoteHost: boolean;
  isNoteAutoSaving: boolean;
  isUpdateNoteLoading: boolean;
  lastSavedTime: DateTime | null;
  noteId: string | null;
  onSaveDraft: () => void;
  onSaveSigned: () => void;
  createNoteError?: string;
  noteAutoSavingError: string;
  uploadInProgressForSignError: boolean;
};

export const EditNoteFooter = ({
  dismissAccordion,
  existingNoteId,
  inquiryId,
  isAnyNoteApiInFlight,
  isGetNoteDataLoading,
  isNoteAuthor,
  isGroupClassNoteHost,
  isNoteAutoSaving,
  isUpdateNoteLoading,
  lastSavedTime,
  onSaveDraft,
  onSaveSigned,
  noteId,
  createNoteError,
  noteAutoSavingError,
  uploadInProgressForSignError,
}: Props) => {
  const { trackEvent } = useAnalytics();
  const { currentUser } = useGlobalContext();
  const [openDeleteDraftModal, setOpenDeleteDraftModal] =
    useState<boolean>(false);

  const [
    deleteNote,
    { isLoading: isDeleteNoteLoading, error: deleteNoteError },
  ] = useDeleteNoteMutation();

  const networkErrorString = useMemo((): string | null => {
    if (createNoteError) {
      return `Unable to ${existingNoteId ? 'fetch' : 'create'} note`;
    }
    if (uploadInProgressForSignError)
      return 'Your attachment is still being uploaded.';
    if (noteAutoSavingError) return 'Unable to autosave';
    if (deleteNoteError) {
      return 'Unable to delete note';
    }

    return null;
  }, [
    createNoteError,
    existingNoteId,
    noteAutoSavingError,
    deleteNoteError,
    uploadInProgressForSignError,
  ]);

  return (
    <>
      <DeleteNoteModal
        isDeleteNoteLoading={isDeleteNoteLoading}
        isOpen={openDeleteDraftModal}
        onConfirm={() => {
          deleteNote({
            authoredByProviderExternalId: currentUser.userId,
            inquiryId,
            noteId,
          }).then(() => {
            setOpenDeleteDraftModal(false);
            dismissAccordion();
          });
        }}
        setIsOpen={setOpenDeleteDraftModal}
      />
      <ChakraFlex
        alignItems="center"
        background="neutral.background.primary"
        justifyContent="space-between"
        width="100%"
      >
        <FireflyIconButton
          ariaLabel="Delete note"
          icon={FAIconName.trashRegular}
          id={`${TOP_LEVEL_ID}_delete-note-btn`}
          isDisabled={isAnyNoteApiInFlight || !isNoteAuthor}
          isLoading={isDeleteNoteLoading}
          onClick={() => {
            trackEvent(NoteEvents.deleteDraftNoteIconClicked, {
              Source: 'Note Form',
            });
            if (isNoteAuthor) setOpenDeleteDraftModal(true);
          }}
          variant="tertiary"
        />
        <ChakraFlex alignItems="center" gap="36px">
          <ChakraBox
            color={networkErrorString ? 'red.500' : 'mono.70'}
            fontSize="12px"
            lineHeight="16.8px"
          >
            <EditNoteAccordionFooterNetworkStatus
              isGetNoteDataLoading={isGetNoteDataLoading}
              isNoteAutoSaving={isNoteAutoSaving}
              isUpdateNoteLoading={isUpdateNoteLoading}
              lastSavedTime={lastSavedTime}
              networkErrorString={networkErrorString}
            />
          </ChakraBox>
          <ChakraBox>
            <FireflyButton
              id={`${TOP_LEVEL_ID}__keep-as-draft`}
              isDisabled={!noteId || isAnyNoteApiInFlight}
              isLoading={isUpdateNoteLoading}
              loadingText="Saving"
              onClick={() => {
                trackEvent(NoteEvents.saveAsDraftNoteFormButtonClicked, {
                  Source: 'Note Form',
                });
                onSaveDraft();
              }}
              variant="tertiary"
            >
              Save as draft
            </FireflyButton>
          </ChakraBox>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__sign-and-finalize`}
            isDisabled={
              !noteId ||
              !(isNoteAuthor || isGroupClassNoteHost) ||
              isAnyNoteApiInFlight
            }
            isLoading={isUpdateNoteLoading}
            leftIcon={FAIconName.checkRegular}
            onClick={() => {
              trackEvent(NoteEvents.signAndFinalizeNoteFormButtonClicked, {
                Source: 'Note Form',
              });
              onSaveSigned();
            }}
          >
            Sign & finalize
          </FireflyButton>
        </ChakraFlex>
      </ChakraFlex>
    </>
  );
};
